import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Chip,
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { LaserMachine } from '../types';
import { useApp } from '../contexts/AppContext';
import { companyService, resellerService } from '../services/api';
import { MACHINE_LICENSES, MachineLicenseKey } from '../utils/licenses';

type LicenseForm = Record<MachineLicenseKey, { enabled: boolean; expiresAt: Dayjs | null }>;

const emptyLicenses = (): LicenseForm => ({
    updateService: { enabled: false, expiresAt: null },
    serviceContract: { enabled: false, expiresAt: null },
    arturo: { enabled: false, expiresAt: null },
});

const licensesFromMachine = (machine: LaserMachine): LicenseForm => {
    const form = emptyLicenses();
    MACHINE_LICENSES.forEach(({ key }) => {
        const expiresAt = machine[`${key}ExpiresAt`];
        form[key] = { enabled: !!machine[`${key}Enabled`], expiresAt: expiresAt ? dayjs(expiresAt) : null };
    });
    return form;
};

interface MachineFormDialogProps {
    open: boolean;
    machine?: LaserMachine | null;
    onClose: () => void;
    onSave: (data: Partial<LaserMachine>) => void;
}

const MachineFormDialog: React.FC<MachineFormDialogProps> = ({
    open,
    machine,
    onClose,
    onSave,
}) => {
    const { state } = useApp();
    const isAdmin = state.auth.user?.role === 'administrator';
    const isReseller = state.auth.user?.role === 'reseller';
    const currentUserId = state.auth.user?.id || '';

    const [formData, setFormData] = useState({
        name: '',
        model: '',
        serialNumber: '',
        location: '',
        ownerId: '',
        resellerId: '',
        power: 0,
        workAreaWidth: 0,
        workAreaHeight: 0,
        maxSpeed: 0,
        supportedMaterials: [] as string[],
        licenseExpirationDate: null as Dayjs | null,
        activationCode: '',
    });

    const [isLifetime, setIsLifetime] = useState(false);
    const [licenses, setLicenses] = useState<LicenseForm>(emptyLicenses);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [materialInput, setMaterialInput] = useState('');

    useEffect(() => {
        if (machine) {
            setFormData({
                name: machine.name,
                model: machine.model,
                serialNumber: machine.serialNumber,
                location: machine.location,
                ownerId: machine.companyId || machine.ownerId || '',
                resellerId: machine.resellerId || '',
                power: machine.specifications?.power || 0,
                workAreaWidth: machine.specifications?.workArea?.width || 0,
                workAreaHeight: machine.specifications?.workArea?.height || 0,
                maxSpeed: machine.specifications?.maxSpeed || 0,
                supportedMaterials: machine.specifications?.supportedMaterials || [],
                licenseExpirationDate: machine.licenseExpirationDate ? dayjs(machine.licenseExpirationDate) : null,
                activationCode: '', // Not used for edits, but required by type
            });
            setIsLifetime(machine.licenseExpirationDate === null || machine.licenseExpirationDate === undefined);
            setLicenses(licensesFromMachine(machine));
        } else {
            // Reset form for new machine
            setFormData({
                name: '',
                model: '',
                serialNumber: '',
                location: '',
                ownerId: '',
                resellerId: '',
                power: 0,
                workAreaWidth: 0,
                workAreaHeight: 0,
                maxSpeed: 0,
                supportedMaterials: [],
                licenseExpirationDate: null,
                activationCode: '',
            });
            setIsLifetime(false);
            setLicenses(emptyLicenses());
        }
        setErrors({});
    }, [machine, open, isAdmin, currentUserId]);

    const validate = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) newErrors.name = 'Nome obbligatorio';
        if (!formData.model.trim()) newErrors.model = 'Modello obbligatorio';
        if (!formData.serialNumber.trim()) newErrors.serialNumber = 'Numero seriale obbligatorio';
        if (!formData.location.trim()) newErrors.location = 'Ubicazione obbligatoria';
        if (!formData.location.trim()) newErrors.location = 'Ubicazione obbligatoria';

        // Activation Code required for Resellers creating new machines
        if (isReseller && !isAdmin && !machine && !formData.activationCode?.trim()) {
            newErrors.activationCode = 'Codice attivazione richiesto';
        }

        // OwnerId is now optional
        if (formData.power <= 0) newErrors.power = 'Potenza deve essere maggiore di 0';
        if (formData.workAreaWidth <= 0) newErrors.workAreaWidth = 'Larghezza area deve essere maggiore di 0';
        if (formData.workAreaHeight <= 0) newErrors.workAreaHeight = 'Altezza area deve essere maggiore di 0';
        if (formData.maxSpeed <= 0) newErrors.maxSpeed = 'Velocità massima deve essere maggiore di 0';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const machineData: Partial<LaserMachine> = {
            name: formData.name,
            model: formData.model,
            serialNumber: formData.serialNumber,
            location: formData.location,
            companyId: formData.ownerId || null, // Backend expects companyId
            ownerId: formData.ownerId || null,
            resellerId: formData.resellerId || undefined,
            licenseExpirationDate: isLifetime ? null : (formData.licenseExpirationDate ? formData.licenseExpirationDate.toDate() : null),
            // Licenze aggiuntive: scadenza vuota = permanente
            ...Object.fromEntries(MACHINE_LICENSES.flatMap(({ key }) => [
                [`${key}Enabled`, licenses[key].enabled],
                [`${key}ExpiresAt`, licenses[key].expiresAt ? licenses[key].expiresAt!.toDate() : null],
            ])),
            specifications: {
                power: formData.power,
                workArea: {
                    width: formData.workAreaWidth,
                    height: formData.workAreaHeight,
                },
                maxSpeed: formData.maxSpeed,
                supportedMaterials: formData.supportedMaterials,
            },
        };

        // Pass activation code if present (needed for API)
        if (formData.activationCode) {
            (machineData as any).activationCode = formData.activationCode;
        }

        onSave(machineData);
    };

    const handleAddMaterial = () => {
        if (materialInput.trim() && !formData.supportedMaterials.includes(materialInput.trim())) {
            setFormData({
                ...formData,
                supportedMaterials: [...formData.supportedMaterials, materialInput.trim()],
            });
            setMaterialInput('');
        }
    };

    const handleDeleteMaterial = (material: string) => {
        setFormData({
            ...formData,
            supportedMaterials: formData.supportedMaterials.filter(m => m !== material),
        });
    };

    // Data state
    const [companies, setCompanies] = useState<any[]>([]);

    useEffect(() => {
        const fetchCompanies = async () => {
            // Only fetch if admin or reseller who can assign owners
            if (isAdmin || state.auth.user?.role === 'reseller') {
                try {
                    const list = await companyService.getCompanies();
                    setCompanies(list);
                } catch (err) {
                    console.error('Failed to load companies', err);
                }
            }
        };
        fetchCompanies();
    }, [isAdmin, state.auth.user, open]);

    // Fetch Resellers (Admin only)
    const [resellers, setResellers] = useState<any[]>([]);
    useEffect(() => {
        const fetchResellers = async () => {
            if (isAdmin) {
                try {
                    const list = await resellerService.getResellers();
                    setResellers(list);
                } catch (err) {
                    console.error('Failed to load resellers', err);
                }
            }
        };
        fetchResellers();
    }, [isAdmin, open]);

    // Use companies for the dropdown
    const clientOptions = companies.map(c => ({ id: c.id, name: c.name }));

    // Resellers list - for now mock or fetch if needed. 
    // If user is admin they can select reseller. 
    // To keep it simple, let's assume admins assign directly to companies (which have resellerId)
    // Or if we need to assign a Reseller, we need a reseller fetch.
    // Given the "OwnerId" is "CompanyId" usually (Machine belongs to Company).
    // The previous code mapped "OwnerId" to "client.id".
    // "clients" were companies.

    // In backend: create machine takes `companyId`.
    // In frontend: formData.ownerId should map to companyId.

    // Let's keep the Reseller selection mock for now or remove if not critical?
    // Project status says: "ResellersManagementPage - CRUD resellers" is done.
    // So we could fetch resellers too. But for "Create Machine", assigning to a Company is key.

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {machine ? 'Modifica Macchina' : 'Nuova Macchina'}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, mt: 1 }}>
                        {/* ... Name, Model, Serial ... */}

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                            <TextField
                                fullWidth
                                label="Nome Macchina"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                error={!!errors.name}
                                helperText={errors.name}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                            <TextField
                                fullWidth
                                label="Modello"
                                value={formData.model}
                                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                error={!!errors.model}
                                helperText={errors.model}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                            <TextField
                                fullWidth
                                label="Numero Seriale"
                                value={formData.serialNumber}
                                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                                error={!!errors.serialNumber}
                                helperText={errors.serialNumber}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                            <TextField
                                fullWidth
                                label="Ubicazione"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                error={!!errors.location}
                                helperText={errors.location}
                                required
                            />
                        </Box>

                        {/* Activation Code for Resellers (Only when creating new) */}
                        {isReseller && !isAdmin && !machine && (
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Codice Attivazione"
                                    value={formData.activationCode || ''}
                                    onChange={(e) => setFormData({ ...formData, activationCode: e.target.value })}
                                    error={!!errors.activationCode}
                                    helperText={errors.activationCode || "Inserisci il codice fornito dall'amministratore"}
                                />
                            </Box>
                        )}

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' } }}>
                            {isAdmin && (
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Rivenditore (Owner)</InputLabel>
                                    <Select
                                        value={formData.resellerId}
                                        onChange={(e) => setFormData({ ...formData, resellerId: e.target.value })}
                                        label="Rivenditore (Owner)"
                                    >
                                        <MenuItem value="">
                                            <em>Nessuno (Diretto)</em>
                                        </MenuItem>
                                        {resellers.map((r) => (
                                            <MenuItem key={r.id} value={r.id}>
                                                {r.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl fullWidth error={!!errors.ownerId}>
                                <InputLabel>Azienda (Cliente)</InputLabel>
                                <Select
                                    value={formData.ownerId}
                                    onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}
                                    label="Azienda (Cliente)"
                                >
                                    <MenuItem value="">
                                        <em>Nessuna assegnata (Magazzino)</em>
                                    </MenuItem>
                                    {clientOptions.length > 0 ? (
                                        clientOptions.map((client) => (
                                            <MenuItem key={client.id} value={client.id}>
                                                {client.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>Caricamento aziende...</MenuItem>
                                    )}
                                </Select>
                                {errors.ownerId && <Typography variant="caption" color="error">{errors.ownerId}</Typography>}
                            </FormControl>
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 6' }, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <DatePicker
                                label="Scadenza Licenza HMI"
                                value={formData.licenseExpirationDate}
                                onChange={(newValue) => setFormData({ ...formData, licenseExpirationDate: newValue })}
                                slotProps={{ textField: { fullWidth: true } }}
                                disabled={isLifetime}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isLifetime}
                                        onChange={(e) => {
                                            setIsLifetime(e.target.checked);
                                            if (e.target.checked) {
                                                setFormData({ ...formData, licenseExpirationDate: null });
                                            }
                                        }}
                                    />
                                }
                                label="Licenza a vita"
                            />
                        </Box>

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <Typography variant="subtitle2">Licenze aggiuntive</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Scadenza vuota = licenza permanente.
                            </Typography>
                        </Box>

                        {MACHINE_LICENSES.map(({ key, label }) => (
                            <Box key={key} sx={{ gridColumn: { xs: '1 / -1', sm: 'span 4' }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={licenses[key].enabled}
                                            onChange={(e) => setLicenses({
                                                ...licenses,
                                                [key]: { ...licenses[key], enabled: e.target.checked },
                                            })}
                                        />
                                    }
                                    label={label}
                                />
                                <DatePicker
                                    label="Scadenza"
                                    value={licenses[key].expiresAt}
                                    onChange={(newValue) => setLicenses({
                                        ...licenses,
                                        [key]: { ...licenses[key], expiresAt: newValue },
                                    })}
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' },
                                        field: { clearable: true },
                                    }}
                                    disabled={!licenses[key].enabled}
                                />
                            </Box>
                        ))}


                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Specifiche Tecniche
                            </Typography>
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3' } }}>
                            <TextField
                                fullWidth
                                label="Potenza (W)"
                                type="number"
                                value={formData.power}
                                onChange={(e) => setFormData({ ...formData, power: Number(e.target.value) })}
                                error={!!errors.power}
                                helperText={errors.power}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3' } }}>
                            <TextField
                                fullWidth
                                label="Area Larghezza (mm)"
                                type="number"
                                value={formData.workAreaWidth}
                                onChange={(e) => setFormData({ ...formData, workAreaWidth: Number(e.target.value) })}
                                error={!!errors.workAreaWidth}
                                helperText={errors.workAreaWidth}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3' } }}>
                            <TextField
                                fullWidth
                                label="Area Altezza (mm)"
                                type="number"
                                value={formData.workAreaHeight}
                                onChange={(e) => setFormData({ ...formData, workAreaHeight: Number(e.target.value) })}
                                error={!!errors.workAreaHeight}
                                helperText={errors.workAreaHeight}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3' } }}>
                            <TextField
                                fullWidth
                                label="Velocità Max (mm/s)"
                                type="number"
                                value={formData.maxSpeed}
                                onChange={(e) => setFormData({ ...formData, maxSpeed: Number(e.target.value) })}
                                error={!!errors.maxSpeed}
                                helperText={errors.maxSpeed}
                                required
                            />
                        </Box>

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Materiali Supportati
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Aggiungi materiale"
                                    value={materialInput}
                                    onChange={(e) => setMaterialInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddMaterial();
                                        }
                                    }}
                                />
                                <Button variant="outlined" onClick={handleAddMaterial}>
                                    Aggiungi
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {formData.supportedMaterials.map((material) => (
                                    <Chip
                                        key={material}
                                        label={material}
                                        onDelete={() => handleDeleteMaterial(material)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={onClose} variant="outlined">
                        Annulla
                    </Button>
                    <Button onClick={handleSave} variant="contained">
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default MachineFormDialog;
